import { useState, useCallback } from 'react';

const STORAGE_KEY = 'hightag_bookmarks';

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return { activities: {}, riders: {} };
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {}
}

/**
 * Manages bookmarks/flags/notes for activities and riders.
 *
 * Activity entry: { note, flagged, tags, createdAt }
 * Rider entry:    { alias, note, flagged, createdAt }
 */
export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState(load);

  const update = useCallback((updater) => {
    setBookmarks(prev => {
      const next = updater(prev);
      save(next);
      return next;
    });
  }, []);

  // ── Activities ──────────────────────────────────────────────────────────

  const flagActivity = useCallback((activityId, flagged) => {
    update(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        [activityId]: {
          note: '',
          tags: [],
          ...prev.activities[activityId],
          flagged,
          createdAt: prev.activities[activityId]?.createdAt || new Date().toISOString(),
        },
      },
    }));
  }, [update]);

  const setActivityNote = useCallback((activityId, note) => {
    update(prev => ({
      ...prev,
      activities: {
        ...prev.activities,
        [activityId]: {
          flagged: false,
          tags: [],
          ...prev.activities[activityId],
          note,
          createdAt: prev.activities[activityId]?.createdAt || new Date().toISOString(),
        },
      },
    }));
  }, [update]);

  const removeActivityBookmark = useCallback((activityId) => {
    update(prev => {
      const next = { ...prev, activities: { ...prev.activities } };
      delete next.activities[activityId];
      return next;
    });
  }, [update]);

  // ── Riders ──────────────────────────────────────────────────────────────

  const flagRider = useCallback((userId, flagged) => {
    update(prev => ({
      ...prev,
      riders: {
        ...prev.riders,
        [userId]: {
          alias: '',
          note: '',
          ...prev.riders[userId],
          flagged,
          createdAt: prev.riders[userId]?.createdAt || new Date().toISOString(),
        },
      },
    }));
  }, [update]);

  const setRiderAlias = useCallback((userId, alias) => {
    update(prev => ({
      ...prev,
      riders: {
        ...prev.riders,
        [userId]: {
          flagged: false,
          note: '',
          ...prev.riders[userId],
          alias,
          createdAt: prev.riders[userId]?.createdAt || new Date().toISOString(),
        },
      },
    }));
  }, [update]);

  const setRiderNote = useCallback((userId, note) => {
    update(prev => ({
      ...prev,
      riders: {
        ...prev.riders,
        [userId]: {
          alias: '',
          flagged: false,
          ...prev.riders[userId],
          note,
          createdAt: prev.riders[userId]?.createdAt || new Date().toISOString(),
        },
      },
    }));
  }, [update]);

  const removeRiderBookmark = useCallback((userId) => {
    update(prev => {
      const next = { ...prev, riders: { ...prev.riders } };
      delete next.riders[userId];
      return next;
    });
  }, [update]);

  return {
    bookmarks,
    flagActivity,
    setActivityNote,
    removeActivityBookmark,
    flagRider,
    setRiderAlias,
    setRiderNote,
    removeRiderBookmark,
  };
}
